import type { ProblemDetails } from "@backchannel/tax-contracts";
import { problemDetailsSchema } from "@backchannel/tax-contracts";
import type { MessageKey } from "~/i18n/messages/pt-BR";
import { failureSentence } from "./failure";

/**
 * `NETWORK`, `TIMEOUT`, `GATEWAY_TIMEOUT` and `UNEXPECTED_RESPONSE` never come
 * from the API's problem contract: the browser (or the gateway in front of the
 * API, which answers with its own non-problem body) raises them.
 */
export type ProblemCode =
  | ProblemDetails["code"]
  | "NETWORK"
  | "TIMEOUT"
  | "GATEWAY_TIMEOUT"
  | "UNEXPECTED_RESPONSE";

/**
 * Every failure the console surfaces is a `TaxApiError`. Screens branch on
 * `code`, not on HTTP status, so the three failure presentations (denied,
 * disabled, unavailable) stay decidable from the contract vocabulary.
 */
export class TaxApiError extends Error {
  readonly code: ProblemCode;
  readonly status: number;
  readonly retryable: boolean;
  readonly problem: ProblemDetails | null;
  readonly requestId: string | undefined;
  readonly correlationId: string | undefined;
  /** HTTP method of the failed request; a failed write may still have committed. */
  readonly method: string | undefined;
  /** Request path, so a screen can tell a safely repeatable write from one that is not. */
  readonly path: string | undefined;
  /** The browser reported no connection, so the request never left the device. */
  readonly offline: boolean;
  /** Seconds to wait before retrying, from a `Retry-After` response header. */
  readonly retryAfterSeconds: number | undefined;

  constructor(init: {
    code: ProblemCode;
    status: number;
    message: string;
    retryable: boolean;
    problem?: ProblemDetails | null;
    requestId?: string;
    correlationId?: string;
    method?: string;
    path?: string;
    offline?: boolean;
    retryAfterSeconds?: number;
  }) {
    super(init.message);
    this.name = "TaxApiError";
    this.code = init.code;
    this.status = init.status;
    this.retryable = init.retryable;
    this.problem = init.problem ?? null;
    this.requestId = init.requestId;
    this.correlationId = init.correlationId;
    this.method = init.method;
    this.path = init.path;
    this.offline = init.offline ?? false;
    this.retryAfterSeconds = init.retryAfterSeconds;
  }

  get violations(): ProblemDetails["violations"] {
    return this.problem?.violations ?? [];
  }
}

const RETRY_AFTER_CAP_SECONDS = 3600;

/**
 * Parses a `Retry-After` header value as seconds to wait. Accepts delta-seconds
 * (`"5"`, `"0"`) and an HTTP-date; anything else is ignored. Fractional results
 * round up; the wait is capped at one hour.
 */
export function parseRetryAfter(value: string | null, nowMs = Date.now()): number | undefined {
  if (value === null) return undefined;
  const trimmed = value.trim();
  if (trimmed === "") return undefined;

  if (/^\d+$/.test(trimmed)) {
    return Math.min(Number(trimmed), RETRY_AFTER_CAP_SECONDS);
  }
  // Negative or fractional numbers are not delay-seconds and must not be
  // interpreted as dates (Date.parse("-1") is a past epoch in some engines).
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return undefined;

  const dateMs = Date.parse(trimmed);
  if (Number.isNaN(dateMs)) return undefined;
  const seconds = Math.ceil((dateMs - nowMs) / 1000);
  return Math.min(Math.max(0, seconds), RETRY_AFTER_CAP_SECONDS);
}

/**
 * Parses an RFC 9457 payload. When the body is not a problem document (the API
 * Gateway answers 413, 429, 502, 503 and 504 itself, in plain text or its own
 * JSON), the code is derived from the HTTP status so the screen can still say
 * what happened and what to do.
 */
export function toTaxApiError(
  status: number,
  body: unknown,
  context: { method?: string; path?: string; retryAfterSeconds?: number } = {},
): TaxApiError {
  const parsed = problemDetailsSchema.safeParse(body);
  if (parsed.success) {
    const problem = parsed.data;
    return new TaxApiError({
      code: problem.code,
      status: problem.status,
      message: problem.detail,
      retryable: problem.retryable,
      problem,
      requestId: problem.requestId,
      correlationId: problem.correlationId,
      method: context.method,
      path: context.path,
      retryAfterSeconds: context.retryAfterSeconds,
    });
  }

  if (status === 404 && isGatewayUndefinedOperation(body)) {
    return new TaxApiError({
      code: "OPERATION_NOT_AVAILABLE",
      status,
      message: gatewayMessage(body) ?? "The current request is not defined by this API.",
      retryable: false,
      method: context.method,
      path: context.path,
      retryAfterSeconds: context.retryAfterSeconds,
    });
  }

  const code = codeForStatus(status, body);
  return new TaxApiError({
    code,
    status,
    message: `Unexpected response (HTTP ${status})`,
    retryable: status >= 500 || status === 408 || status === 429,
    method: context.method,
    path: context.path,
    retryAfterSeconds: context.retryAfterSeconds,
  });
}

function codeForStatus(status: number, body: unknown): ProblemCode {
  if (status === 0) return "NETWORK";
  if (status === 504 || isGatewayResponseTimeout(body)) return "GATEWAY_TIMEOUT";
  switch (status) {
    case 400:
    case 422:
      return "VALIDATION_FAILED";
    case 401:
      return "UNAUTHENTICATED";
    case 403:
      return "PERMISSION_DENIED";
    case 404:
      return "NOT_FOUND";
    case 408:
      return "TIMEOUT";
    case 409:
      return "VERSION_PRECONDITION_FAILED";
    case 413:
      return "BATCH_TOO_LARGE";
    case 429:
      return "RATE_LIMITED";
    case 502:
    case 503:
      return "DEPENDENCY_UNAVAILABLE";
    default:
      return "INTERNAL_ERROR";
  }
}

/** API Gateway / Envoy deadline text, which can arrive on a non-504 status. */
function isGatewayResponseTimeout(body: unknown): boolean {
  const text = typeof body === "string" ? body : gatewayMessage(body);
  return Boolean(text && /response_timeout|upstream request timeout|deadline exceeded/i.test(text));
}

function gatewayMessage(body: unknown): string | null {
  if (!body || typeof body !== "object" || !("message" in body)) return null;
  return typeof body.message === "string" ? body.message : null;
}

/** Google API Gateway 404 when the live spec does not yet list the path. */
function isGatewayUndefinedOperation(body: unknown): boolean {
  const message = gatewayMessage(body);
  return Boolean(message && /not defined by this API/i.test(message));
}

export function networkError(
  cause: unknown,
  context: { method?: string; path?: string; timedOut?: boolean; offline?: boolean } = {},
): TaxApiError {
  return new TaxApiError({
    code: context.timedOut ? "TIMEOUT" : "NETWORK",
    status: 0,
    message: cause instanceof Error ? cause.message : "Network request failed",
    retryable: true,
    method: context.method,
    path: context.path,
    offline: context.offline,
  });
}

/**
 * The browser's network error for a request that waited until the gateway's deadline. API Gateway
 * answers its own 504 without CORS headers, so the page only sees "Failed to fetch" (status 0);
 * after that long a wait it is the server running out of time, not the person's connection, and it
 * is reported (and replayed) as the gateway timeout it is.
 */
export function gatewayDeadlineError(
  cause: unknown,
  context: { method?: string; path?: string } = {},
): TaxApiError {
  return new TaxApiError({
    code: "GATEWAY_TIMEOUT",
    status: 504,
    message: cause instanceof Error ? cause.message : "Gateway deadline",
    retryable: true,
    method: context.method,
    path: context.path,
  });
}

/** A 2xx whose body is not what the screen can read (HTML shell, empty, wrong shape). */
export function unexpectedResponseError(
  cause: unknown,
  context: { method?: string; path?: string; status?: number } = {},
): TaxApiError {
  return new TaxApiError({
    code: "UNEXPECTED_RESPONSE",
    status: context.status ?? 200,
    message: cause instanceof Error ? cause.name : "Unexpected response",
    retryable: true,
    method: context.method,
    path: context.path,
  });
}

const CODE_MESSAGE_KEYS: Record<ProblemCode, MessageKey> = {
  UNAUTHENTICATED: "errors.UNAUTHENTICATED",
  PERMISSION_DENIED: "errors.PERMISSION_DENIED",
  ENVIRONMENT_MISMATCH: "errors.ENVIRONMENT_MISMATCH",
  CONTEXT_MISMATCH: "errors.VALIDATION_FAILED",
  IDEMPOTENCY_KEY_REUSED: "errors.VALIDATION_FAILED",
  IDEMPOTENCY_IN_PROGRESS: "errors.RATE_LIMITED",
  IDEMPOTENCY_LEASE_LOST: "errors.RATE_LIMITED",
  NO_PUBLISHED_RULESET: "errors.NO_PUBLISHED_RULESET",
  RULESET_STALE_BASE: "errors.RULESET_STALE_BASE",
  SEPARATION_OF_DUTIES_VIOLATION: "errors.SEPARATION_OF_DUTIES_VIOLATION",
  VERSION_PRECONDITION_FAILED: "errors.VERSION_PRECONDITION_FAILED",
  BATCH_TOO_LARGE: "errors.BATCH_TOO_LARGE",
  VALIDATION_FAILED: "errors.VALIDATION_FAILED",
  RATE_LIMITED: "errors.RATE_LIMITED",
  DEPENDENCY_UNAVAILABLE: "errors.DEPENDENCY_UNAVAILABLE",
  ARTIFACT_UNAVAILABLE: "errors.ARTIFACT_UNAVAILABLE",
  // Runtime-only (web research worker); never shown by the admin surface.
  RUN_ACTIVE: "errors.OPERATION_NOT_AVAILABLE",
  LEASE_LOST: "errors.VERSION_PRECONDITION_FAILED",
  OPERATION_PRICING_UNRESOLVED: "errors.OPERATION_PRICING_UNRESOLVED",
  OPERATION_NOT_AVAILABLE: "errors.OPERATION_NOT_AVAILABLE",
  NOT_FOUND: "errors.NOT_FOUND",
  INTERNAL_ERROR: "errors.INTERNAL_ERROR",
  NETWORK: "errors.NETWORK",
  TIMEOUT: "errors.TIMEOUT",
  GATEWAY_TIMEOUT: "errors.GATEWAY_TIMEOUT",
  UNEXPECTED_RESPONSE: "errors.UNEXPECTED_RESPONSE",
};

export function messageKeyForError(error: unknown): MessageKey {
  if (error instanceof TaxApiError) {
    return CODE_MESSAGE_KEYS[error.code] ?? "errors.INTERNAL_ERROR";
  }
  return "errors.INTERNAL_ERROR";
}

/**
 * Operator-facing text for a failure. API errors read the catalogue message
 * for their code, so the English problem detail never reaches the screen;
 * local failures (file reads, parsing) keep their own text.
 */
export function errorText(
  t: (key: MessageKey) => string,
  error: unknown,
  fallback: MessageKey = "errors.INTERNAL_ERROR",
): string {
  if (error instanceof TaxApiError) return failureSentence(t, error);
  if (error instanceof Error && error.message) return error.message;
  return t(fallback);
}

/**
 * Maps a failure onto one of the three distinct presentations the design
 * system defines. `denied` is about the caller, `unavailable` is about the
 * platform, `error` is everything else worth retrying or reporting.
 */
export function failureKindFor(error: unknown): "denied" | "unavailable" | "error" {
  if (!(error instanceof TaxApiError)) return "error";
  if (error.code === "PERMISSION_DENIED" || error.code === "UNAUTHENTICATED") return "denied";
  if (
    error.code === "DEPENDENCY_UNAVAILABLE" ||
    error.code === "OPERATION_NOT_AVAILABLE" ||
    error.code === "RATE_LIMITED" ||
    error.code === "TIMEOUT" ||
    error.code === "GATEWAY_TIMEOUT"
  ) {
    return "unavailable";
  }
  return "error";
}
